const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export interface FocusTrap {
  release(): void;
}

/**
 * The European Accessibility Act treats the consent dialog as part of the service, so
 * keyboard operability is a legal requirement, not polish. Focus moves in, cycles inside,
 * and returns to wherever it came from on release.
 *
 * Escape is wired up by the caller rather than here: dismissing the *first layer* must not
 * count as a decision, while dismissing the preferences layer simply goes back.
 */
export function trapFocus(container: HTMLElement): FocusTrap {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Tab") return;
    const targets = focusable(container);
    if (targets.length === 0) return;

    const first = targets[0];
    const last = targets[targets.length - 1];
    if (!first || !last) return;

    const active = activeElementIn(container);
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  container.addEventListener("keydown", onKeyDown);
  focusable(container)[0]?.focus();

  return {
    release() {
      container.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    },
  };
}

function focusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

/** Inside a shadow root `document.activeElement` is the host, so ask the root instead. */
function activeElementIn(container: HTMLElement): Element | null {
  const root = container.getRootNode();
  if (root instanceof ShadowRoot) return root.activeElement;
  return document.activeElement;
}

export function onEscape(container: HTMLElement, handler: () => void): () => void {
  const listener = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      handler();
    }
  };
  container.addEventListener("keydown", listener);
  return () => container.removeEventListener("keydown", listener);
}
